import React, { useState, useEffect, useMemo } from 'react';
import { CollectionReptile, Pairing, blankPairing } from '../collection/types/CollectionTypes.ts';
import { Button, Table, FormControl, InputGroup, Alert } from 'react-bootstrap';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPen, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ReptoolModal } from '../utilities/ReptoolModal.tsx';
import { AddReptile } from '../clutches/modals/AddClutchModal.tsx';
import { RepToolDropdown } from '../utilities/shared_components/RepToolDropdown.tsx';
import { CollectionReptileModalSettings } from '../collection/modals/CollectionReptileModal.tsx';
import { LoadingSpinner } from '../Loading.tsx';
import { SelectCollectionReptileModal } from "../clutches/modals/SelectCollectionReptileModal.tsx"
import { FeatureRequiresSubscription, SubscriptionModal } from '../stripe/SubscriptionModal.tsx';
import { UserInfo } from '../../types/UserInfo.ts';
import { useRepToolContext } from '../../RepToolContext.tsx';

export const CreatePairingModal = ({
  collectionReptile,
  closeModal,
  editingPairing,
  selectedCollectionReptileMatch,
  setSelectedCollectionReptileMatch,
  setCollectionReptileSettings,
  openSubscriptionModal,
  userInfo
}: {
  collectionReptile?: CollectionReptile,
  closeModal: () => void,
  editingPairing: Pairing,
  selectedCollectionReptileMatch: CollectionReptile | null,
  setSelectedCollectionReptileMatch: (reptile: CollectionReptile | null) => void;
  setCollectionReptileSettings: (settings: CollectionReptileModalSettings) => void;
  openSubscriptionModal: () => void;
  userInfo: UserInfo
}) => {
  const [currentPairing, setCurrentPairing] = useState<Pairing>(editingPairing || blankPairing);
  const [creating, setCreating] = useState(false);
  const { appendControls } = useRepToolContext();
  const needsSubscription = useMemo(() => {
    return userInfo.subscription?.subscription_type
  }, [userInfo.subscription])

  const isEditing = useMemo(() => {
    return editingPairing.id !== -1;
  }, [editingPairing]);

  const createPairing = () => {
    if (!currentPairing.mother || !currentPairing.father) {
      (window as any).alert("Please select a mother and father");
      return;
    }
    setCreating(true);
    const data = new FormData();
    data.append("mother_id", currentPairing.mother.id.toString());
    data.append("father_id", currentPairing.father.id.toString());
    data.append("notes", currentPairing.notes);
    data.append("paired_date", new Date(currentPairing.paired_date).toDateString());
    data.append("separation_date", new Date(currentPairing.separation_date).toDateString());
    const url = isEditing ? `${(window as any).serverLocation}collection_reptile/pairing/${editingPairing.id}/edit` : `${(window as any).serverLocation}collection_reptile/pairing/create`;
    BasicFetch({
      url,
      method: isEditing ? 'PATCH' : 'POST',
      data,
      contentType: 'multipart/form-data',
      onSuccess: () => {
        closeModal();
      },
      onFail: () => {
        (window as any).alert("There was an error updating this pairing, please try again later");
      },
      onFinish: () => {
        setCreating(false);
      }
    })
  }

  const setUpCalendar = (editingKey: "paired_date" | "separation_date", editingDate: string) => {
    (window as any).setUpCalendar(
      editingKey.charAt(0).toUpperCase() + editingKey.slice(1).replace("_", " "),
      () => (date: Date) => {
        setCurrentPairing({ ...currentPairing, [editingKey]: date });
        (window as any).closeCalendar();
      },
      new Date(editingDate)
    );
  }

  useEffect(() => {
    if (editingPairing) {
      setCurrentPairing(editingPairing);
    }
  }, [editingPairing]);

  useEffect(() => {
    if (collectionReptile?.gender === "female") {
      setCurrentPairing({
        ...currentPairing,
        mother: collectionReptile
      })
    } else if (collectionReptile?.gender === "male") {
      setCurrentPairing({
        ...currentPairing,
        father: collectionReptile
      })
    }
  }, [collectionReptile]);

  useEffect(() => {
    if (selectedCollectionReptileMatch) {
      if (selectedCollectionReptileMatch.gender === "male") {
        setCurrentPairing({
          ...currentPairing,
          father: selectedCollectionReptileMatch
        })
      } else if (selectedCollectionReptileMatch.gender === "female") {
        setCurrentPairing({
          ...currentPairing,
          mother: selectedCollectionReptileMatch
        })
      }
      setSelectedCollectionReptileMatch(null);
    }
  }, [selectedCollectionReptileMatch]);

  useEffect(() => {
    if (needsSubscription) {
      appendControls(null);
      return
    }
    if (creating) {
      appendControls(<div className='d-flex mx-auto'>
        <Button id='create-pairing-button' variant='outline-secondary' className='mx-auto'>
          <FontAwesomeIcon icon={faSpinner} spin />
        </Button>
      </div>, "Create Pairing Modal");
    } else {
      appendControls(<div className='d-flex mx-auto'>
        <Button id='create-pairing-button' variant='outline-light' className='mx-auto' onClick={createPairing}>{isEditing ? 'Edit' : 'Create'} Pairing</Button>
      </div>, "Create Pairing Modal");
    }
  }, [selectedCollectionReptileMatch, creating, currentPairing]);

  return (
    <ReptoolModal
      id='create-pairing-modal'
      closeModal={closeModal}
      title={isEditing ? "Edit Pairing" : "Create Pairing"}
      body={<>
        {needsSubscription ? <FeatureRequiresSubscription clickFunction={openSubscriptionModal} /> :
          <div className='text-center d-flex flex-column'>
            <div className='d-flex mx-auto col-md-6 col-lg-4 justify-content-around'>
              <div className='form-group'>
                <label>Sire</label>
                <div onClick={() => !collectionReptile || currentPairing.father?.id !== collectionReptile?.id ? setCollectionReptileSettings({ sexFilter: "male" }) : null}>
                  <AddReptile reptile={currentPairing.father} />
                </div>
              </div>
              <div className='form-group'>
                <label>Dam</label>
                <div onClick={() => !collectionReptile || currentPairing.mother?.id !== collectionReptile?.id ? setCollectionReptileSettings({ sexFilter: "female" }) : null}>
                  <AddReptile reptile={currentPairing.mother} />
                </div>
              </div>
            </div>
            <div className='d-flex mx-auto justify-content-around col-md-6 col-lg-4'>
              <div className='form-group'>
                <h6>Paired On:</h6>
                <div className='border border-light rounded py-1 d-flex justify-content-between' onClick={() => setUpCalendar("paired_date", currentPairing.paired_date)}>
                  <span className='ml-2'>{new Date(currentPairing.paired_date).toLocaleDateString()}</span>
                  <FontAwesomeIcon className='my-auto mx-3 text-light' icon={faPen} />
                </div>
              </div>
              <div>
                <h6>Separated On:</h6>
                <div className='border border-light rounded py-1 d-flex' onClick={() => setUpCalendar("separation_date", currentPairing.separation_date)}>
                  <span className='ml-2'>{new Date(currentPairing.separation_date).toLocaleDateString()}</span>
                  <FontAwesomeIcon className='my-auto mx-3 text-light' icon={faPen} />
                </div>
              </div>
            </div>
            <div className='d-flex flex-column col-md-8 mx-auto mt-3'>
              <label>Notes</label>
              <textarea
                className='form-control bg-dark text-white'
                value={currentPairing.notes}
                rows={5}
                onChange={(e) => setCurrentPairing({ ...currentPairing, notes: e.target.value })}
              />
            </div>
          </div>}
      </>
      }
    />
  )
}

const PairingComponent = ({
  pairing,
  openPairingModal,
  fetchPairings,
  onSelect
}: {
  pairing: Pairing,
  openPairingModal?: (pairing?: Pairing) => void,
  fetchPairings: () => void,
  onSelect?: (pairing: Pairing) => void
}) => {

  const deletePairing = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/pairing/${pairing.id}/delete`,
      method: 'DELETE',
      onSuccess: () => {
        fetchPairings();
      },
      onFail: () => {
        (window as any).alert("There was an error loading this reptile, please try again later");
      },
    })
  }


  return (
    <div className='border p-2 col-12 col-md-11 mx-auto rounded mt-2'>
      <div className='d-flex flex-md-row flex-column justify-content-between'>
        <div className='d-flex flex-row col-md-3 my-auto justify-content-between'>
          <div className='mr-2'>
            <div>
              <h5 className='mb-0 text-center'>Dam</h5>
            </div>
            {pairing.mother && <AddReptile reptile={pairing.mother} borderless />}
          </div>
          <div>
            <div>
              <h5 className='mb-0 text-center'>Sire</h5>
            </div>
            {pairing.father && <AddReptile reptile={pairing.father} borderless />}
          </div>
        </div>
        <div className='d-flex flex-column px-2 mx-2 flex-fill' >
          <Table className='blank-table' size='sm'>
            <tbody>
              <tr>
                <td>Paired:</td>
                <td>{new Date(pairing.paired_date).toDateString()}</td>
              </tr>
              <tr>
                <td>Separated:</td>
                <td>{new Date(pairing.separation_date).toDateString()}</td>
              </tr>
            </tbody>
          </Table>
          <div>{pairing.notes}</div>
        </div>
        <div className='d-flex flex-column'>
          <div className='my-auto mr-3 ml-auto'>
            {!onSelect ? <RepToolDropdown
              className='mt-3 d-flex py-3'
              options={[
                {
                  label: "Edit",
                  onClick: () => openPairingModal?.(pairing)
                },
                {
                  label: "Delete",
                  onClick: deletePairing
                }
              ]}
            /> : <Button variant='outline-light' onClick={() => onSelect(pairing)}>Select</Button>}
          </div>
        </div>
      </div>
    </div>
  )
}

export const AllPairingsModal = ({
  closeModal,
  userInfo,
}: {
  userInfo: UserInfo
  closeModal: () => void
}) => {
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [editingPairing, setEditingPairing] = useState<Pairing | undefined>(undefined);
  const [collectionReptileSettings, setCollectionReptileSettings] = useState<CollectionReptileModalSettings | null>(null);
  const [selectedCollectionReptileMatch, setSelectedCollectionReptileMatch] = useState<CollectionReptile | null>(null);

  return (
    <>
      {openSubscriptionModal && <SubscriptionModal displayFeatureRequiresMessage={false} userInfo={userInfo} closeModal={() => setOpenSubscriptionModal(false)} />}

      {
        collectionReptileSettings &&
        <SelectCollectionReptileModal
          closeModal={() => setCollectionReptileSettings(null)}
          onSelect={(reptile) => {
            setEditingPairing({ ...editingPairing, [collectionReptileSettings.sexFilter]: reptile });
            setCollectionReptileSettings(null);
            setSelectedCollectionReptileMatch(reptile);
          }}
          sexFilter={collectionReptileSettings.sexFilter}
        />
      }
      {editingPairing && <CreatePairingModal
        userInfo={userInfo}
        openSubscriptionModal={() => setOpenSubscriptionModal(true)}
        closeModal={() => setEditingPairing(undefined)}
        editingPairing={editingPairing}
        selectedCollectionReptileMatch={selectedCollectionReptileMatch}
        setSelectedCollectionReptileMatch={setSelectedCollectionReptileMatch}
        setCollectionReptileSettings={setCollectionReptileSettings}
      />}

      <ReptoolModal
        id='all-pairings-modal'
        closeModal={closeModal}
        title='All Pairings'
        body={
          <div className='mb-5'>
            <Pairings
              readOnly={false}
              openPairingModal={(pairing) => setEditingPairing(pairing)}
              refetchPairings={!editingPairing}
            />
          </div>
        }
      />
    </>

  )
}

export const Pairings = ({
  currentReptile,
  openPairingModal,
  refetchPairings,
  onSelect,
  readOnly = false,
}: {
  currentReptile?: CollectionReptile,
  openPairingModal?: (pairing?: Pairing | null) => void,
  refetchPairings: boolean,
  onSelect?: (pairing: Pairing) => void,
  readOnly?: boolean,
}) => {
  const [fetching, setFetching] = React.useState<boolean>(false);
  const [pairings, setPairings] = React.useState<Pairing[]>([]);
  const [sort, setSort] = React.useState<"asc" | "desc">("asc");
  const [search, setSearch] = React.useState<string>("");

  const fetchPairings = () => {
    setFetching(true);
    const url = currentReptile ? `${(window as any).serverLocation}collection_reptile/${currentReptile.id}/pairings` : `${(window as any).serverLocation}collection_reptile/pairings`;
    BasicFetch({
      url,
      method: 'GET',
      onSuccess: (data: { pairings: Pairing[] }) => {
        setPairings(data.pairings);
      },
      onFail: () => {
        (window as any).alert("There was an error loading pairings, please try again later");
      },
      onFinish: () => {
        setFetching(false);
      }
    })
  }


  useEffect(() => {
    fetchPairings();
  }, [currentReptile, refetchPairings]);


  const filteredPairings = useMemo(() => {
    // we want to filter by search by name of the reptile or genes or morph
    return pairings.filter((pairing) => {
      const mother = pairing.mother ? pairing.mother.name.toLowerCase() : "";
      const father = pairing.father ? pairing.father.name.toLowerCase() : "";

      const motherGenes = pairing.mother ? pairing.mother.genes.map(gene => gene.gene_name.toLowerCase()) : [];
      const fatherGenes = pairing.father ? pairing.father.genes.map(gene => gene.gene_name.toLowerCase()) : [];

      const motherMorph = pairing.mother && pairing.mother.morph_name ? pairing.mother.morph_name.toLowerCase() : "";
      const fatherMorph = pairing.father && pairing.father.morph_name ? pairing.father.morph_name.toLowerCase() : "";

      const searchLower = search.toLowerCase();

      return mother.includes(searchLower)
        || father.includes(searchLower)
        || motherGenes.filter(gene => gene.includes(searchLower)).length
        || fatherGenes.filter(gene => gene.includes(searchLower)).length
        || motherMorph.includes(searchLower)
        || fatherMorph.includes(searchLower);


    }).sort((a, b) => {
      if (sort === "asc") {
        return new Date(a.paired_date).getTime() - new Date(b.paired_date).getTime();
      } else {
        return new Date(b.paired_date).getTime() - new Date(a.paired_date).getTime();
      }
    });
  }, [pairings, search, sort]);


  return (
    <div>
      <div className='d-flex'>
        <InputGroup>
          <FormControl
            className='flex-fill mx-auto my-3 bg-dark text-white'
            placeholder='Search'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <RepToolDropdown
            className='mx-auto my-3'
            toggleComponent={<>Sort</>}
            options={[
              {
                label: "Ascending",
                onClick: () => setSort("asc")
              },
              {
                label: "Descending",
                onClick: () => setSort("desc")
              }
            ]}
          />
        </InputGroup>

      </div>
      {!fetching && <div className='text-center mb-3'>
        Displaying {pairings.length} pairings sorted by {sort === "asc" ? "ascending" : "descending"} paired date
      </div>}
      {fetching && <div className='text-center'>
        <div className='mb-3'>Fetching Pairings</div>
      </div>
      }
      {!fetching && !pairings.length && <Alert variant='info' className='text-center'>
        <div className='d-flex'>
          <FontAwesomeIcon icon={faExclamationTriangle} size='2x' className='my-auto mr-3' />
          <div className='my-auto d-flex flex-column mx-auto'>
            <h5 className='text-primary'>No Pairings Found</h5>
            {!openPairingModal ? <p className='mb-0'>You can create a pairing from the collection page.</p> : <p className='mb-0'>Click the button below to create a pairing.</p>}
          </div>
        </div>
      </Alert>
      }
      {
        fetching && !pairings.length && <div className='text-center'>
          <div className='m-auto d-flex justify-content-center' style={{
            maxWidth: 200,
            maxHeight: 200
          }}
          >
            <LoadingSpinner />
          </div>
        </div>
      }
      {
        !fetching && !readOnly && openPairingModal && <div className='border border-dashed mt-3 p-3 col-12 col-md-8 mx-auto mb-4 d-flex rounded flex-md-row flex-column justify-content-center'
          onClick={() => openPairingModal(blankPairing)}
          style={{
            cursor: 'pointer'
          }}>
          <FontAwesomeIcon size='2x' className='mb-2' icon={faPlus} />
          <span className='ml-md-3 my-auto mx-auto'>Create Pairing</span>
        </div>
      }
      {filteredPairings.map((pairing) => {
        return (
          <PairingComponent
            pairing={pairing}
            openPairingModal={openPairingModal}
            fetchPairings={fetchPairings}
            onSelect={onSelect}
          />
        )
      })}
    </div>
  )
}